<template>
  <WebInput
    ref="cpfCnpjInput"
    v-model="identityCardNumberEl"
    :class-name="inputClass"
    :tab-index="inputTabIndex"
    :placeholder="translate('generate.placeholder.cpfCnpj', locale)"
    :name="`${name}_Pix`"
    type="mask"
    :mask="cpfCnpjMask"
    inputmode="numeric"
    :label="getInputLabel(translate('generate.label.cpfCnpj', locale), isLabelVisible)"
    :label-style="labelStyle"
    :disabled="isSubmitDisabled || isSubmitLoading || disabled"
    :rules="required ? {required: true, cpfCnpj: true} : undefined"
    :page-options="pageOptions"
    autocomplete="on"
    @error="handleInputError"
  />
</template>

<script lang="ts" setup>
import { ref, toRefs, computed, watch } from 'vue';
import WebInput from '@zotlo-shared/components/input/index.vue';
import { useTranslate } from '@zotlo-shared/composable/useTranslate';
import { useLabelOptions } from '@zotlo-shared/composable/useLabelOptions';
import type { Element as ElementType, PageOptions } from '@zotlo-shared/types/model';
import { isClient } from '@zotlo-shared/utils/helpers';

const emit = defineEmits(['update:identityCardNumber', 'error', 'validCpfCnpjEntered']);
const props = withDefaults(defineProps<{
  identityCardNumber: string;
  element: ElementType<'registration' | 'payment' | 'payment-with-tabs'>,
  pageOptions: PageOptions;
  pageData: Record<string, any>;
  name?: string;
  locale?: string;
  isEditorMode?: boolean;
  disabled?: boolean;
  required?: boolean;
  className?: string | Record<string, boolean> | Array<any>;
}>(), {
  locale: '',
  cpfCnpj: '',
  name: 'cpfCnpj',
  required: true,
  className: '',
});

// Dynamic mask: CPF (###.###.###-##) for values up to 11 digits, CNPJ (##.###.###/####-##) for 12-14 digits.
const cpfCnpjMask = [
  { mask: '000.000.000-00' },
  { mask: '00.000.000/0000-00' }
];

const { element, pageOptions } = toRefs(props);
const { translate } = useTranslate();
const { labelStyle, isLabelVisible } = useLabelOptions({ element, pageOptions });
const cpfCnpjInput = ref();

const {
  identityCardNumber: cpfCnpjProp
} = toRefs(props);

const identityCardNumberEl = ref(cpfCnpjProp.value);

const inputTabIndex = computed(() => (props.isEditorMode ? '-1' : undefined));
const inputClass = computed(() => [
  'mb-16',
  { 'pointer-events-none': props.isEditorMode },
  props.className
]);
const isSubmitLoading = computed(() => props.pageData?.isSubmitLoading);
const isSubmitDisabled = computed(() => props.pageData?.isSubmitDisabled);

if (isClient()) {
  watch(
    () => identityCardNumberEl.value,
    async (val) => {
      emit('update:identityCardNumber', val);
      if (await isCpfCnpjValid()) emit('validCpfCnpjEntered', val);
    }
  );

  watch(
    () => [cpfCnpjProp.value],
    ([cpfCnpjValue]) => {
      if (cpfCnpjValue !== identityCardNumberEl.value) {
        identityCardNumberEl.value = cpfCnpjValue || '';
      }
    }
  );
}

function handleInputError(errMessage: string) {
  emit('error', errMessage);
}

function getInputLabel(label = '', show = true) {
  return show ? label : '';
}

async function isCpfCnpjValid() {
  const validationMeta = await cpfCnpjInput.value?.getValidationMeta();
  if (!validationMeta) return false;
  return validationMeta?.valid;
}
</script>
